import React, { createContext, PropsWithChildren, useState, useEffect, useCallback, useMemo } from 'react'
import { Auth, Hub } from 'aws-amplify'
import { CognitoUser } from '@aws-amplify/auth'

export type AuthContextType = {
  user: CognitoUser | null | undefined
  groups: string[]
  isOwner: boolean
  isOwnerOrAdmin: boolean
  setUser: React.Dispatch<React.SetStateAction<CognitoUser | null | undefined>>
  loading: boolean
  removalRole: string | null
  isRemovalSpecialist: boolean
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  groups: [],
  isOwner: false,
  isOwnerOrAdmin: false,
  setUser: () => {},
  loading: true,
  removalRole: null,
  isRemovalSpecialist: false
})

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<CognitoUser | null>()
  const [groups, setGroups] = useState<string[]>([])
  const [removalRole, setRemovalRole] = useState<string | null>(null)
  const fetchUserAndGroups = useCallback(async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser()
      const userGroups = currentUser.signInUserSession.idToken.payload['cognito:groups'] || []
      const userRemovalRole = currentUser.attributes["custom:removal_role"] || null
      setUser(currentUser)
      setGroups(userGroups)
      setRemovalRole(userRemovalRole)
    } catch (error) {
      console.error('Error fetching authenticated user or groups:', error)
      setUser(null)
      setGroups([])
    }
  }, [])

  useEffect(() => {
    fetchUserAndGroups()

    const listener = ({ payload: { event } }: any) => {
      if (event === 'signIn') {
        fetchUserAndGroups()
      } else if (event === 'signOut') {
        setUser(null)
        setGroups([])
        setRemovalRole(null)
      }
    }

    Hub.listen('auth', listener)
    return () => {
      Hub.remove('auth', listener)
    }
  }, [fetchUserAndGroups])

  const loading = user === undefined || groups.length === 0

  const { isOwner, isOwnerOrAdmin, isRemovalSpecialist } = useMemo(() => {
    const owner = groups.includes('Owner')
    const ownerOrAdmin = owner || groups.includes('Admin')
    const removalSpecialist = removalRole === 'removal_specialist';
    return { isOwner: owner, isOwnerOrAdmin: ownerOrAdmin, isRemovalSpecialist: removalSpecialist }
  }, [groups, removalRole])

  return (
    <AuthContext.Provider value={{ user, groups, isOwner, isOwnerOrAdmin, setUser, loading, removalRole, isRemovalSpecialist }}>
      {children}
    </AuthContext.Provider>
  )
}
